import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFlight } from '../contexts/FlightContext';
import { CalendarIcon, PaperAirplaneIcon, BellIcon, ArrowLeftOnRectangleIcon, RestartIcon } from './Icons';
import { UserRole } from '../types';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { selectedFlightData: flightData, startAndEndSession, selectedFlightSlotId } = useFlight();
  const currentDate = new Date().toLocaleDateString('en-GB');
  const [dismissedTimestamp, setDismissedTimestamp] = useState<number | null>(null);
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);

  const flightNumber = flightData?.counterDistribution?.flightNo || 'N/A';

  const lastNotification = flightData?.lastNotification;
  const showNotificationInHeader = lastNotification && lastNotification.timestamp !== dismissedTimestamp;
  
  const handleClearNotification = () => {
    if (lastNotification) {
      setDismissedTimestamp(lastNotification.timestamp);
    }
  };
  
  const handleStartOver = async () => {
      if (selectedFlightSlotId) {
          await startAndEndSession(selectedFlightSlotId);
          // No need to close modal, the component will unmount
      }
  };

  if (!flightData) return null; // Don't render header if no flight is selected

  return (
      <>
        <header className="relative bg-slate-900/70 backdrop-blur-lg shadow-lg text-slate-300 no-print flex-shrink-0 z-30">
          <div className="container mx-auto flex justify-between items-center px-2 sm:px-4 py-3">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <PaperAirplaneIcon className="w-8 h-8 text-blue-500 -rotate-45 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-white to-slate-400 text-transparent bg-clip-text truncate">JT-Crew Movement</h1>
                <div className="flex items-center space-x-3 text-xs text-slate-400 mt-1">
                  <div className="flex items-center space-x-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{currentDate}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <PaperAirplaneIcon className="w-4 h-4" />
                    <span className="font-bold">{flightNumber}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {showNotificationInHeader && lastNotification && (
                <div className="relative group">
                  <button onClick={handleClearNotification} className="flex flex-col items-center w-12 sm:w-14 text-amber-400 p-1 rounded-lg hover:bg-slate-700">
                    <div className="relative">
                      <BellIcon className="w-6 h-6 animate-intense-pulse" />
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">!</span>
                    </div>
                    <span className="text-[9px] sm:text-[10px] mt-1">Alert</span>
                  </button>
                  <div className="absolute top-full right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-20 p-3 hidden group-hover:block animate-slide-up-fade-in text-sm text-left">
                    <p className="font-bold text-white">{lastNotification.station}</p>
                    <p className="text-slate-300">Recorded at {lastNotification.time} by {lastNotification.triggeredBy}</p>
                    <p className="text-xs text-slate-500 mt-2">Click bell to dismiss this notification.</p>
                  </div>
                </div>
              )}
              
              {user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && (
                <button
                    onClick={() => setShowStartOverConfirm(true)}
                    data-tour-id="new-session-button"
                    className="flex flex-col items-center w-12 sm:w-14 text-slate-400 hover:text-white p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500"
                    title="Start Over"
                >
                    <RestartIcon className="w-5 h-5" />
                    <span className="text-[9px] sm:text-[10px] mt-1">Start Over</span>
                </button>
              )}

              <button
                onClick={logout}
                className="flex flex-col items-center w-12 sm:w-14 text-slate-400 hover:text-white p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500"
                title="Logout"
              >
                <ArrowLeftOnRectangleIcon className="w-5 h-5" />
                <span className="text-[9px] sm:text-[10px] mt-1">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {showStartOverConfirm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
              <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                  <h2 className="text-xl font-bold text-white mb-4">Confirm Start Over</h2>
                  <p className="text-slate-300 mb-6">
                      This will save the current flight data as a report and start a new, empty session. Are you sure you want to continue?
                  </p>
                  <div className="flex justify-center gap-4">
                      <button onClick={() => setShowStartOverConfirm(false)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
                      <button onClick={handleStartOver} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">Confirm & Start Over</button>
                  </div>
              </div>
          </div>
        )}
      </>
  );
};

export default Header;